import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Save,
  User,
  Phone,
  CheckCircle,
  Info,
  Users,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const PARTICIPANT_TYPES = [
  "SMS über Mobilfunk",
  "Sprache über Mobilfunk",
  "Sprache über Festnetz",
];

const MESSAGE_TYPES = [
  "Störung Prio 1",
  "Störung Prio 2",
  "Personenalarm",
  "Gerätestörung",
  "Testmeldung",
];

const COUNTRY_CODES = [
  { code: "+49", country: "Deutschland", flag: "🇩🇪" },
  { code: "+43", country: "Österreich", flag: "🇦🇹" },
  { code: "+41", country: "Schweiz", flag: "🇨🇭" },
  { code: "+31", country: "Niederlande", flag: "🇳🇱" },
  { code: "+32", country: "Belgien", flag: "🇧🇪" },
  { code: "+33", country: "Frankreich", flag: "🇫🇷" },
  { code: "+39", country: "Italien", flag: "🇮🇹" },
  { code: "+44", country: "Großbritannien", flag: "🇬🇧" },
  { code: "+45", country: "Dänemark", flag: "🇩🇰" },
  { code: "+46", country: "Schweden", flag: "🇸🇪" },
  { code: "+47", country: "Norwegen", flag: "🇳🇴" },
  { code: "+48", country: "Polen", flag: "🇵🇱" },
  { code: "+420", country: "Tschechien", flag: "🇨🇿" },
  { code: "+352", country: "Luxemburg", flag: "🇱🇺" },
];

const TeamForm = () => {
  const { teamToken } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [projectStatus, setProjectStatus] = useState("pending");
  const [projectName, setProjectName] = useState("");
  const [isValid, setIsValid] = useState(false);
  
  const [formData, setFormData] = useState({
    project_name: "",
    customer_name: "",
    date: new Date().toLocaleDateString("de-DE"),
    participants: Array.from({ length: 10 }, (_, i) => ({
      number: i + 1,
      type: "",
      name: "",
      country_code: "+49",
      phone: "",
    })),
    sequences: Array.from({ length: 8 }, (_, i) => ({
      line: i + 1,
      message_type: "",
      message_text: "",
      assignments: [],
      cycle_count: 12,
      cycle_time: 30,
    })),
    confirmation: false,
    signature: null,
  });

  useEffect(() => {
    validateTokenAndFetchForm();
  }, [teamToken]);

  const validateTokenAndFetchForm = async () => {
    try {
      // First validate the token
      const validateRes = await axios.get(`${API_URL}/api/team/${teamToken}`);
      if (!validateRes.data.valid) {
        toast.error("Ungültiger Team-Link");
        navigate("/login");
        return;
      }
      
      setProjectName(validateRes.data.project_name);
      setProjectStatus(validateRes.data.status);
      setIsValid(true);
      
      if (validateRes.data.status === "submitted") {
        toast.error("Dieses Formular wurde bereits abgeschlossen");
        return;
      }

      // Fetch the form data
      const formRes = await axios.get(`${API_URL}/api/team/${teamToken}/form`);
      const data = formRes.data;
      
      const participants = (data.participants || formData.participants).map((p) => ({
        ...p,
        country_code: p.country_code || "+49",
      }));
      
      const sequences = (data.sequences || formData.sequences).map((s) => ({
        ...s,
        cycle_count: s.cycle_count ?? 12,
        cycle_time: s.cycle_time ?? 30,
      }));
      
      setFormData({
        project_name: data.project_name || "",
        customer_name: data.customer_name || "",
        date: data.date || new Date().toLocaleDateString("de-DE"),
        participants,
        sequences,
        confirmation: data.confirmation || false,
        signature: data.signature || null,
      });
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Laden des Formulars";
      toast.error(message);
      if (error.response?.status === 404 || error.response?.status === 400) {
        navigate("/login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/api/team/${teamToken}/form`, formData);
      toast.success("Änderungen gespeichert");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Speichern";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const updateParticipant = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      ),
    }));
  };

  const formatPhoneNumber = (value) => {
    return value.replace(/[^\d]/g, "").slice(0, 15);
  };

  const updateSequence = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sequences: prev.sequences.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      ),
    }));
  };

  const updateSequenceAssignment = (sequenceIndex, assignmentIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      sequences: prev.sequences.map((s, i) => {
        if (i !== sequenceIndex) return s;
        
        const newAssignments = [...(s.assignments || [])];
        while (newAssignments.length <= assignmentIndex) {
          newAssignments.push({ order: newAssignments.length + 1, value: "" });
        }
        newAssignments[assignmentIndex] = { order: assignmentIndex + 1, value };
        
        return { ...s, assignments: newAssignments };
      }),
    }));
  };

  const getAvailableParticipants = () => {
    return formData.participants.filter((p) => p.name && p.phone);
  };

  const isSubmitted = projectStatus === "submitted";

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-slate-600">Ungültiger oder abgelaufener Team-Link</p>
            <Button onClick={() => navigate("/login")} className="mt-4">
              Zur Anmeldung
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="plutec" className="h-8" />
              <span className="font-manrope font-bold text-lg text-[#1e3a5f]">
                comXline
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-500">Team-Zugriff</span>
            </div>
          </div>
        </div>
      </header>

      {/* Team Access Banner */}
      <div className="bg-blue-50 border-b border-blue-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-blue-600" />
            <p className="text-blue-800">
              <strong>Team-Bearbeitung:</strong> Sie können das Formular bearbeiten. Die finale Unterschrift erfolgt durch den Hauptkontakt.
            </p>
          </div>
        </div>
      </div>

      {/* Submitted Banner */}
      {isSubmitted && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <p className="text-emerald-800 font-medium">
                Dieses Formular wurde bereits abgeschlossen.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-2xl text-slate-900 mb-2">
            Anwahlfolgen-Konfiguration
          </h1>
          <p className="text-slate-500">
            Projekt: <strong>{projectName}</strong> – Bearbeiten Sie das Formular gemeinsam mit Ihrem Team.
          </p>
        </div>

        {/* Project Info */}
        <Card className="card-base mb-6" data-testid="project-info-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-manrope text-lg">Projektdaten</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <Label className="label-style">Projektbezeichnung</Label>
                <p className="mt-1 font-medium text-slate-900">{formData.project_name}</p>
              </div>
              <div>
                <Label className="label-style">Kundenname</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, customer_name: e.target.value }))
                  }
                  disabled={isSubmitted}
                  className="mt-1"
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label className="label-style">Datum</Label>
                <p className="mt-1 font-medium text-slate-900">{formData.date}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Participants Table */}
        <Card className="card-base mb-6" data-testid="participants-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-manrope text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              Teilnehmer
            </CardTitle>
            <CardDescription>
              Legen Sie bis zu 10 Teilnehmer an, die später den Anwahlfolgen zugeordnet werden können.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-16 text-center">Nr.</TableHead>
                    <TableHead className="w-48">Teilnehmertyp</TableHead>
                    <TableHead>Teilnehmer-Name</TableHead>
                    <TableHead className="w-32">Vorwahl</TableHead>
                    <TableHead className="w-40">Rufnummer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.participants.map((participant, index) => (
                    <TableRow key={participant.number} className="table-row">
                      <TableCell className="text-center font-mono font-semibold text-slate-600">
                        {participant.number}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={participant.type}
                          onValueChange={(value) => updateParticipant(index, "type", value)}
                          disabled={isSubmitted}
                        >
                          <SelectTrigger className="table-select" data-testid={`participant-type-${index}`}>
                            <SelectValue placeholder="Auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {PARTICIPANT_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={participant.name}
                          onChange={(e) => updateParticipant(index, "name", e.target.value)}
                          placeholder="Name eingeben..."
                          className="table-input"
                          disabled={isSubmitted}
                          data-testid={`participant-name-${index}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={participant.country_code}
                          onValueChange={(value) => updateParticipant(index, "country_code", value)}
                          disabled={isSubmitted}
                        >
                          <SelectTrigger className="table-select" data-testid={`participant-country-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRY_CODES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.flag} {c.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={participant.phone}
                          onChange={(e) =>
                            updateParticipant(index, "phone", formatPhoneNumber(e.target.value))
                          }
                          placeholder="123456789"
                          className="table-input font-mono"
                          disabled={isSubmitted}
                          data-testid={`participant-phone-${index}`}
                          inputMode="numeric"
                          pattern="[0-9]*"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Sequences Table */}
        <Card className="card-base mb-6" data-testid="sequences-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-manrope text-lg flex items-center gap-2">
              <Phone className="w-5 h-5 text-slate-600" />
              Anwahlfolgen
            </CardTitle>
            <CardDescription>
              Konfigurieren Sie die Anwahlfolgen für jede Meldelinie.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-16 text-center">Linie</TableHead>
                    <TableHead className="w-40">Meldung</TableHead>
                    <TableHead>Teilnehmer-Zuordnung (Reihenfolge)</TableHead>
                    <TableHead className="w-24 text-center">Zyklen</TableHead>
                    <TableHead className="w-24 text-center">Zeit (s)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.sequences.map((sequence, seqIndex) => (
                    <TableRow key={sequence.line} className="table-row align-top">
                      <TableCell className="text-center font-mono font-semibold text-slate-600 pt-4">
                        {sequence.line}
                      </TableCell>
                      <TableCell className="pt-3">
                        <Select
                          value={sequence.message_type}
                          onValueChange={(value) => updateSequence(seqIndex, "message_type", value)}
                          disabled={isSubmitted}
                        >
                          <SelectTrigger className="table-select" data-testid={`sequence-type-${seqIndex}`}>
                            <SelectValue placeholder="Auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {MESSAGE_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {sequence.message_type === "Testmeldung" && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                            <Info className="w-3 h-3" />
                            Erfolgt täglich um 13:00 Uhr
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="pt-3">
                        <div className="flex flex-wrap gap-2">
                          {[0, 1, 2, 3, 4].map((assignIndex) => (
                            <Select
                              key={assignIndex}
                              value={sequence.assignments?.[assignIndex]?.value || ""}
                              onValueChange={(value) =>
                                updateSequenceAssignment(seqIndex, assignIndex, value)
                              }
                              disabled={isSubmitted}
                            >
                              <SelectTrigger 
                                className="w-40 table-select text-xs" 
                                data-testid={`sequence-assign-${seqIndex}-${assignIndex}`}
                              >
                                <SelectValue placeholder={`${assignIndex + 1}. Wahl`} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="keine Teilnehmer">
                                  keine Teilnehmer
                                </SelectItem>
                                {getAvailableParticipants().map((p) => (
                                  <SelectItem key={p.number} value={`${p.number} – ${p.name}`}>
                                    {p.number} – {p.name}
                                  </SelectItem>
                                ))}
                                <SelectItem value="---" disabled>
                                  — Quittierung durch —
                                </SelectItem>
                                <SelectItem value="einen Teilnehmer">
                                  einen Teilnehmer
                                </SelectItem>
                                <SelectItem value="alle Teilnehmer">
                                  alle Teilnehmer
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="pt-3">
                        <Input
                          type="number"
                          min="1"
                          value={sequence.cycle_count || ""}
                          onChange={(e) =>
                            updateSequence(seqIndex, "cycle_count", parseInt(e.target.value) || null)
                          }
                          placeholder="12"
                          className="table-input text-center font-mono"
                          disabled={isSubmitted}
                          data-testid={`sequence-cycles-${seqIndex}`}
                        />
                      </TableCell>
                      <TableCell className="pt-3">
                        <Input
                          type="number"
                          min="1"
                          value={sequence.cycle_time || ""}
                          onChange={(e) =>
                            updateSequence(seqIndex, "cycle_time", parseInt(e.target.value) || null)
                          }
                          placeholder="30"
                          className="table-input text-center font-mono"
                          disabled={isSubmitted}
                          data-testid={`sequence-time-${seqIndex}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Note about Signature */}
        {!isSubmitted && (
          <Card className="card-base mb-6 bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Hinweis zur Unterschrift</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Die finale Unterschrift und das Abschließen des Formulars erfolgt durch den Hauptkontakt, 
                    der die Einladung erhalten hat. Als Team-Mitglied können Sie die Daten bearbeiten und speichern.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {!isSubmitted && (
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving}
              data-testid="save-btn"
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></span>
                  Speichern...
                </span>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Änderungen speichern
                </>
              )}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default TeamForm;
